/**
 * Helpers for the "upload your design" (offline PDF) pages in cb-ui and fedex-ui. Both upload the
 * same way: the PDF goes into the container as design/offline_{side}.pdf and is converted into
 * Texture_0 (outside) / Texture_2 (inside) PNGs.
 */
export type UploadSide = "outside" | "inside";

/** The pixels per inch the PDF is converted at (a 14.78 x 14 in net comes back 2957 x 2800 px). */
export const OFFLINE_ART_DPI = 200;

/** Where the container keeps a side's PDF (inside the design/ folder). */
export function offlinePdfName(side: UploadSide): string {
  return `offline_${side}.pdf`;
}

/**
 * Die-line guide (cut/bleed/fold lines) PNG for a container's side - the same per-container files
 * the design editor overlays (see gridlinesUrlFor() in design-3d-redesign).
 */
export function dielineGuideUrl(apiHost: string, containerId: string, side: UploadSide): string {
  const filename = side === "inside" ? "gridlines_inside.png" : "gridlines.png";
  return `${apiHost}/project/container/${containerId}/${filename}`;
}

export interface StoredUpload {
  side: UploadSide;
  /** The PDF's size in bytes; 0 when the container does not say. */
  size: number;
  /** The converted texture's base64 content, when the container has one. */
  texture?: string;
}

/**
 * The PDFs already in a container for the given sides, from a getContainerFiles() listing. A design
 * reopened later (Edit from review, Continue) has its art there, but the upload page only tracks
 * files uploaded during the current visit.
 */
export function findStoredUploads(files: any[], sides: UploadSide[]): StoredUpload[] {
  const find = (name: string) => files.find((file) => (file?.name ?? "").toString().split("/").pop() === name);
  const found: StoredUpload[] = [];
  for (const side of sides) {
    const pdf = find(offlinePdfName(side)) ?? find(`uploaded_${side}.pdf`);
    if (!pdf) {
      continue;
    }
    const n = side === "inside" ? 2 : 0;
    const texture = find(`Texture_${n}_original.png`) ?? find(`Texture_${n}.png`);
    found.push({ side, size: Number(pdf.length) || 0, texture: texture?.content || undefined });
  }
  return found;
}

/**
 * How much of the converted image, from its top left, is the net. A PDF can be a little bigger than
 * the net (extra white below or beside the artwork); that part is not shown, so the die-line guide,
 * which covers the net exactly, lines up with what is on screen. 1 when the PDF is the net's size.
 */
export function netFraction(inches: number | undefined, pixels: number): number {
  if (!inches || !pixels) {
    return 1;
  }
  return Math.min(1, (inches * OFFLINE_ART_DPI) / pixels);
}

/** A file size for display: "640 KB", "2.4 MB"; empty when unknown. */
export function formatFileSize(bytes: number | undefined): string {
  if (!bytes) {
    return "";
  }
  return bytes < 1024 * 1024 ? `${Math.max(1, Math.round(bytes / 1024))} KB` : `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
